'use client';

import { useTheme } from 'next-themes';
import { useCallback, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { META_THEME_COLORS } from '@/config/site';
import { useIsClient } from '@/hooks/use-is-client';
import { useMetaColor } from '@/hooks/use-meta-color';
import { playSfx } from '@/hooks/use-sound';

import type { MoonIconHandle } from './animated-icons/moon';
import { MoonIcon } from './animated-icons/moon';
import type { SunMediumIconHandle } from './animated-icons/sun-medium';
import { SunMediumIcon } from './animated-icons/sun-medium';
import { Tooltip, TooltipContent, TooltipTrigger } from './base/ui/tooltip';
import { Button } from './ui/button';
import { Kbd } from './ui/kbd';

export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const isClient = useIsClient();

  const { setMetaColor } = useMetaColor();

  const moonIconRef = useRef<MoonIconHandle>(null);
  const sunIconRef = useRef<SunMediumIconHandle>(null);

  const switchTheme = useCallback(() => {
    playSfx('toggle');
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
    setMetaColor(
      resolvedTheme === 'dark'
        ? META_THEME_COLORS.light
        : META_THEME_COLORS.dark
    );
  }, [resolvedTheme, setTheme, setMetaColor]);

  const handleMouseEnter = useCallback(() => {
    moonIconRef.current?.startAnimation();
    sunIconRef.current?.startAnimation();
  }, []);

  const handleMouseLeave = useCallback(() => {
    moonIconRef.current?.stopAnimation();
    sunIconRef.current?.stopAnimation();
  }, []);

  // enableOnFormTags/enableOnContentEditable default to false, but guard
  // explicitly so 't' never fires while the user is typing anywhere.
  useHotkeys(
    't',
    () => {
      const el = document.activeElement;
      if (
        el instanceof HTMLElement &&
        (el.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
      ) {
        return;
      }
      switchTheme();
    },
    { enableOnFormTags: false, enableOnContentEditable: false },
    [switchTheme]
  );

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            data-sfx-managed=""
            onClick={switchTheme}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            // onClick={() => {
            //   if (!document.startViewTransition) switchTheme();
            //   document.startViewTransition(switchTheme);
            // }}
          />
        }
      >
        <MoonIcon
          ref={moonIconRef}
          className="relative hidden after:absolute after:-inset-2 [html.dark_&]:block"
        />
        <SunMediumIcon
          ref={sunIconRef}
          className="relative hidden after:absolute after:-inset-2 [html.light_&]:block"
        />
        {/* resolvedTheme is unknown during SSR (it lives in localStorage), so
            the first client render must match the server's default branch;
            the stateful label appears right after mount. */}
        <span className="sr-only">
          {isClient && resolvedTheme === 'dark'
            ? 'Switch to light theme'
            : 'Switch to dark theme'}
        </span>
      </TooltipTrigger>

      <TooltipContent className="pr-2 pl-3">
        <div className="flex items-center gap-3">
          Toggle Mode
          <Kbd>T</Kbd>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
